import { useCallback } from 'react';
import { ApiError, NetworkError } from './client.ts';
import { useI18n } from '../i18n/index.tsx';

/**
 * What to put on the screen when something failed.
 *
 * Every screen used to do this itself, and each did it slightly differently:
 * most rendered `error.message` — the server's English — one fell back to a
 * generic sentence, one checked for a rate limit. Three shapes of failure
 * exist and a customer can tell them apart, so they are named here once:
 *
 *  - the request never arrived, which is about their connection;
 *  - the server refused it and said why, in their language;
 *  - something else went wrong, which is ours and cannot be explained.
 */
export function useApiError(): (error: unknown) => string {
  const { t, pick } = useI18n();
  return useCallback(
    (error: unknown): string => {
      if (error instanceof NetworkError) return t('error.network');
      if (error instanceof ApiError) {
        // Said separately because "too many attempts" is the one refusal
        // where waiting is the answer, and the server's sentence does not say
        // for how long.
        if (error.isRateLimited) return t('error.rateLimited');
        // `localized` is what the server wrote for this customer. `message`
        // is the same sentence in English, kept for the failures that are
        // re-thrown from somewhere with nothing translated to offer.
        return error.localized ? pick(error.localized) : error.message;
      }
      return t('common.somethingWentWrong');
    },
    [t, pick],
  );
}
